import React, { useEffect } from 'react';

import { Button, Container, Header, Link, Spinner } from '@amzn/awsui-components-react';
import { Auth, Hub } from 'aws-amplify';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { BottomNav } from './components/bottomNav';
import KatalMetrics from './components/katalAnalytics';
import { ResetQueriesOnPageChange } from './components/queryResetter';
import { TopNav } from './components/topNav';

import './App.scss';
import { GateOnFeatureLoad, GateOnUserLoad } from './components/context';
import { externalLinks } from './data/externalLinks';
import { POST_LOGIN_PATH_KEY } from './hooks/login/login';
import { UNAUTHORIZED } from './utils/constants';

export function LoadSsoButton() {
  const { t } = useTranslation();
  return (
    <Container data-testid="loading-spinner" className="fa-3x">
      <Header>{t('auth.title')}</Header>
      <Button
        variant="primary"
        onClick={() =>
          Auth.federatedSignIn({
            customProvider: 'AmazonFederate',
            customState: window.location.href,
          })
        }
      >
        {t('auth.login')}
      </Button>
    </Container>
  );
}

function UnauthorizedUser() {
  const { t } = useTranslation();

  return (
    <Container data-testid="unauthorized">
      <Header>{t('auth.failed')}</Header>
      <p>
        <Trans
          i18nKey="auth.failedInfo"
          t={t}
          components={{
            wikiLink: (
              <Link
                external
                variant="primary"
                data-aci-analytics-name={UNAUTHORIZED + '_wikiLink'}
                href={externalLinks.support.whoCanAccess}
              />
            ),
            contactLink: (
              <Link
                external
                variant="primary"
                data-aci-analytics-name={UNAUTHORIZED + '_contactLink'}
                href={externalLinks.support.contact}
              />
            ),
          }}
        />
      </p>
    </Container>
  );
}

function Loading() {
  return (
    <Container data-testid="loading-spinner" className="fa-3x">
      <Spinner />
    </Container>
  );
}

function App() {
  // We are just hammering the issue here - possible we only need to intercept one of these events,
  // but redirecting on any of them to be sure that we do so.
  useEffect(() => {
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'oAuthSignIn':
        case 'signIn':
          const returnPath = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
          if (returnPath) {
            const urlValid = new URL(returnPath);
            window.location.assign(decodeURI(urlValid.href));
            sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
          }
          break;
        default:
          return;
      }
    });
  });

  return (
    <GateOnUserLoad
      loading={() => <Loading />}
      error={() => <LoadSsoButton />}
      unauthorized={() => <UnauthorizedUser />}
      authorized={() => (
        <GateOnFeatureLoad
          loading={() => <Loading />}
          render={() => (
            <div className="App" data-testid="app-container">
              <div className="header">
                <KatalMetrics />
                <TopNav />
              </div>
              <main>
                <ResetQueriesOnPageChange />
                <Outlet />
              </main>
              <BottomNav />
            </div>
          )}
        />
      )}
    />
  );
}

export default App;
